import axios from "axios";
import { ref } from "vue";
import { defineStore } from "pinia";
import { API_URL } from "@/utils/constans";
import WebApp from '@twa-dev/sdk';

export const useRequestsStore = defineStore("request", () => {
    const loaderBtn = ref(false);
    const isLoading = ref(false);
    const userData = ref(null);
    const profileLoaded = ref(false);
    const responseError = ref("");
    const availableAttempts = ref(0);
    const currentIdempotencyKey = ref(null);
    // 'code' | 'spin' | 'custom' | null — тип ошибки для TheWarningPage
    const errorType = ref(null);
    const lastActivateResult = ref(null);
    const errorCustomMessage = ref('');

    const INIT_DATA_KEY = 'tg_init_data';
    // Минимальная длина валидного initData — короче это заглушка SDK
    const INIT_DATA_MIN_LEN = 20;

    // Сохраняет валидный initData в sessionStorage и возвращает лучший доступный
    const getInitData = () => {
        const live = WebApp.initData;
        if (live && live.length >= INIT_DATA_MIN_LEN) {
            sessionStorage.setItem(INIT_DATA_KEY, live);
            return live;
        }
        // При обновлении страницы SDK даёт короткую заглушку — берём сохранённый
        return sessionStorage.getItem(INIT_DATA_KEY) || '';
    };

    // Ждёт пока появится валидный initData (либо из SDK, либо из sessionStorage)
    const waitForInitData = (timeoutMs = 8000) => {
        return new Promise((resolve) => {
            const cached = sessionStorage.getItem(INIT_DATA_KEY);
            if (cached && cached.length >= INIT_DATA_MIN_LEN) { resolve(cached); return; }
            if (WebApp.initData && WebApp.initData.length >= INIT_DATA_MIN_LEN) {
                resolve(getInitData()); return;
            }
            const start = Date.now();
            const interval = setInterval(() => {
                if (WebApp.initData && WebApp.initData.length >= INIT_DATA_MIN_LEN) {
                    clearInterval(interval);
                    resolve(getInitData());
                } else if (Date.now() - start >= timeoutMs) {
                    clearInterval(interval);
                    resolve(getInitData());
                }
            }, 50);
        });
    };

    // Устанавливает X-Telegram-Init-Data перед каждым защищённым запросом
    const setAuthHeader = () => {
        const initData = getInitData();
        if (initData) {
            axios.defaults.headers.common['X-Telegram-Init-Data'] = initData;
        } else {
            delete axios.defaults.headers.common['X-Telegram-Init-Data'];
        }
    };

    axios.interceptors.response.use(
        (response) => response,
        (error) => {
            if (error.response?.status === 401) {
                delete axios.defaults.headers.common['X-Telegram-Init-Data'];
            }
            return Promise.reject(error);
        }
    );

    // ── Профиль участника ────────────────────────────────────────────────────────
    // Повторный вызов возвращает кэш — реальный запрос только при первом вызове
    const fetchProfile = async () => {
        if (import.meta.env.VITE_DEV_BYPASS_AUTH === 'true') {
            if (!userData.value) {
                userData.value = { available_attempts: 5, language: 'ru', prizes: [], codes: [] };
                availableAttempts.value = 5;
                profileLoaded.value = true;
            }
            return userData.value;
        }
        if (profileLoaded.value) return userData.value;
        await waitForInitData();
        setAuthHeader();
        try {
            const { data } = await axios.get(`${API_URL}/api/v1/profile`);
            userData.value = data;
            availableAttempts.value = data.available_attempts;
            profileLoaded.value = true;
            return data;
        } catch (error) {
            if (error.response?.status === 401) {
                console.warn("fetchProfile: 401 Невалидный Telegram initData");
            } else {
                console.error("fetchProfile:", error);
            }
            return null;
        }
    };

    const refreshProfile = async () => {
        profileLoaded.value = false;
        return fetchProfile();
    };

    // ── Смена языка ──────────────────────────────────────────────────────────────
    const updateLanguage = async (lang) => {
        setAuthHeader();
        try {
            await axios.post(`${API_URL}/api/v1/profile/language`, { language: lang });
            return true;
        } catch (error) {
            console.error("updateLanguage:", error);
            return false;
        }
    };

    // ── Активация кода с крышки ──────────────────────────────────────────────────
    // outcome: 'activated' | 'not_found' | 'already_used' | 'blocked'
    const activateCode = async (code) => {
        setAuthHeader();
        loaderBtn.value = true;
        responseError.value = "";
        try {
            const { data } = await axios.post(`${API_URL}/api/v1/codes/activate`, { code });
            lastActivateResult.value = data;
            return data;
        } catch (error) {
            const status = error.response?.status;
            if (status === 429) {
                responseError.value = "tooManyAttempts";
            } else {
                responseError.value = error.response?.data?.detail || "error";
            }
            return null;
        } finally {
            loaderBtn.value = false;
        }
    };

    // ── История активированных кодов ─────────────────────────────────────────────
    const fetchCodes = async () => {
        setAuthHeader();
        try {
            const { data } = await axios.get(`${API_URL}/api/v1/codes`);
            return data.codes;
        } catch (error) {
            console.error("fetchCodes:", error);
            return [];
        }
    };

    // ── Спин (один оборот шкафа) ─────────────────────────────────────────────────
    // outcome: 'won' | 'empty' | 'no_attempts' | 'already_processed'
    const spin = async () => {
        currentIdempotencyKey.value = crypto.randomUUID();
        setAuthHeader();
        isLoading.value = true;
        try {
            const { data } = await axios.post(`${API_URL}/api/v1/spins`, {
                idempotency_key: currentIdempotencyKey.value,
            });
            availableAttempts.value = data.new_attempts;
            if (data.outcome === 'no_attempts') {
                responseError.value = 'noAttempts';
            }
            return data;
        } catch (error) {
            const status = error.response?.status;
            if (status === 429) responseError.value = "rateLimitExceeded";
            else if (status === 404) responseError.value = "notRegistered";
            else if (status === 403) responseError.value = "promoNotActive";
            else responseError.value = error.response?.data?.detail || "error";
            return null;
        } finally {
            isLoading.value = false;
        }
    };

    // ── Детали выигранного приза ─────────────────────────────────────────────────
    // fulfillmentId — ID из prize_fulfillment, не catalog id
    const fetchPrizeDetail = async (fulfillmentId) => {
        setAuthHeader();
        try {
            const { data } = await axios.get(`${API_URL}/api/v1/prizes/${fulfillmentId}`);
            return data;
        } catch (error) {
            console.error("fetchPrizeDetail:", error);
            return null;
        }
    };

    // ── Партнёрские промокоды (Setanta и др.) ────────────────────────────────────
    const fetchPartnerCodes = async () => {
        setAuthHeader();
        try {
            const { data } = await axios.get(`${API_URL}/api/v1/partner-codes`);
            return data.items;
        } catch (error) {
            console.error("fetchPartnerCodes:", error);
            return [];
        }
    };

    // ── Доступные размеры футболок ────────────────────────────────────────────────
    const fetchTshirtSizes = async () => {
        setAuthHeader();
        try {
            const { data } = await axios.get(`${API_URL}/api/v2/tshirt-sizes`);
            return data;
        } catch {
            return null;
        }
    };

    // ── Кастомизация футболки ─────────────────────────────────────────────────────
    // Шаг 1: tshirt_caption + tshirt_size
    const customizeTshirt = async (fulfillmentId, payload) => {
        setAuthHeader();
        loaderBtn.value = true;
        responseError.value = "";
        errorCustomMessage.value = "";
        try {
            const { data } = await axios.post(
                `${API_URL}/api/v2/prizes/${fulfillmentId}/customize-tshirt`,
                payload
            );
            return data;
        } catch (error) {
            const status = error.response?.status;
            const detail = error.response?.data?.detail;
            if (status === 422 && Array.isArray(detail)) {
                errorCustomMessage.value = detail.map(e => e.msg).join(', ');
            } else if (typeof detail === 'string') {
                errorCustomMessage.value = detail;
            } else {
                errorCustomMessage.value = '';
            }
            return null;
        } finally {
            loaderBtn.value = false;
        }
    };

    // ── Адрес доставки ────────────────────────────────────────────────────────────
    // Шаг 2: recipient_full_name + recipient_phone + pickup_point_id
    const customizeAddress = async (fulfillmentId, payload) => {
        setAuthHeader();
        loaderBtn.value = true;
        responseError.value = "";
        errorCustomMessage.value = "";
        try {
            const { data } = await axios.post(
                `${API_URL}/api/v2/prizes/${fulfillmentId}/customize-address`,
                payload
            );
            return data;
        } catch (error) {
            const status = error.response?.status;
            const detail = error.response?.data?.detail;
            if (status === 422 && Array.isArray(detail)) {
                errorCustomMessage.value = detail.map(e => e.msg).join(', ');
            } else if (typeof detail === 'string') {
                errorCustomMessage.value = detail;
            } else {
                errorCustomMessage.value = '';
            }
            return null;
        } finally {
            loaderBtn.value = false;
        }
    };

    const withLoader = async (fn) => {
        isLoading.value = true;
        try { return await fn(); }
        finally { isLoading.value = false; }
    };

    return {
        setAuthHeader,
        waitForInitData,
        fetchProfile,
        refreshProfile,
        updateLanguage,
        activateCode,
        fetchCodes,
        spin,
        fetchPrizeDetail,
        fetchPartnerCodes,
        fetchTshirtSizes,
        customizeTshirt,
        customizeAddress,
        withLoader,

        loaderBtn,
        isLoading,
        userData,
        profileLoaded,
        responseError,
        availableAttempts,
        currentIdempotencyKey,
        errorType,
        lastActivateResult,
        errorCustomMessage,
    };
});
